var ThumbnailVariables            = require("app/theme/styles/thumbnails/ThumbnailVariables"),
    config                        = require("app/config"),
    createBunnyImageUrl           = require("app/utils/createBunnyImageUrl"),
    createThumborImageUrl         = require("app/utils/createThumborImageUrl"),
    findBestFittingCover          = require("app/utils/findBestFittingCover"),
    findBestFittingPlaceholder    = require("app/utils/findBestFittingPlaceholder"),
    findBestFittingThumbnail      = require("app/utils/findBestFittingThumbnail"),
    get                           = require("object-path").get,
    placeholders                  = require("app/theme/assets/placeholders"),
    relativeImageUrlToAbsoluteUrl = require("app/utils/relativeImageUrlToAbsoluteUrl"),
    themeVars                     = require("app/theme/variables.js"),
    imageUrlGenerator,
    createArtwork,
    createPoster,
    createThumbnail,
    createEndOfVideoPoster;

// Determine which image optimizer to use
switch (get(config, "imageOptimizer.service", null)) {
    case "thumbor":
        imageUrlGenerator = createThumborImageUrl;
        break;
    case "bunny":
        imageUrlGenerator = createBunnyImageUrl;
        break;
    default:
        imageUrlGenerator = null;
        break;
}

/**
 * Create metadata configuration for Radiant Media Player from the provided
 * media item.
 *
 * Available options:
 * - artwork (boolean):  Whether to include `artwork` property
 * - poster (boolean):  Whether to include `poster` property
 * - thumbnail (boolean):  Whether to include `thumbnail` property
 * - endOfVideoPoster (boolean):  Whether to include `endOfVideoPoster` property
 * - artworkRatio (number):  The aspect ratio of the artwork images
 * - posterRatio (number):  The aspect ratio of the poster images
 * - thumbnailRatio (number):  The aspect ratio of the thumbnail image
 *
 * @param    {Object}  media    The media item
 * @param    {Object}  options  Metadata options
 * @returns  {Object}  The metadata config
 */
module.exports         = function(media, options) {
    var title           = get(media, "meta.title", null),
        artists         = get(media, "artists", []),
        album,
        artwork,
        poster,
        thumbnail,
        endOfVideoPoster,
        contentMetadata = {},
        setup           = {};

    if (title) {
        contentMetadata.title = title;
    }

    if (artists) {
        contentMetadata.artist = artists.join(", ");
    }

    switch (get(media, "type", null)) {
        case "episode":
            album = get(media, "parent.parent.meta.title", null);
            break;
        case "album_track":
        case "podcast_episode":
            album = get(media, "parent.meta.title", null);
            break;
        default:
            album = null;
            break;
    }

    if (album) {
        contentMetadata.album = album;
    }

    if (options.artwork) {
        artwork = createArtwork(media, options);

        if (artwork.length) {
            contentMetadata.artwork = artwork;
        }
    }

    if (options.poster) {
        poster = createPoster(media, options);

        if (poster.length) {
            contentMetadata.poster = poster;
        }
    }

    if (options.thumbnail) {
        thumbnail = createThumbnail(media, options);

        if (thumbnail) {
            contentMetadata.thumbnail = thumbnail;
        }
    }

    if (options.endOfVideoPoster) {
        endOfVideoPoster = createEndOfVideoPoster();

        if (endOfVideoPoster.length) {
            contentMetadata.endOfVideoPoster = endOfVideoPoster;
        }
    }

    setup.contentMetadata = contentMetadata;

    return setup;
};

/**
 * Creates an array of artworks for Radiant MP. Options object can be used to
 * configure which aspect ratio to use.
 *
 * @param    {Object}  media    The media item
 * @param    {Object}  options  The artwork options
 * @returns  {Array}   An array of artworks for Radiant MP
 */
createArtwork = function(media, options) {
    var artworks    = [],
        aspectRatio = ThumbnailVariables.getAspectRatio(options.artworkRatio || "square"),
        artwork     = findBestFittingThumbnail(media, aspectRatio);

    if (artwork && imageUrlGenerator) {
        [/* 96, 128, 192, 256, 384 */512].forEach(function(size) {
            artworks.push({
                src: imageUrlGenerator(
                    relativeImageUrlToAbsoluteUrl(artwork.url),
                    size,
                    size,
                    "png"
                ),
                sizes: [size, "x", size].join(""),
                type:  "image/png"
            });
        });
    }

    return artworks;
};

/**
 * Creates a url for the poster for Radiant MP. Options object can be used to
 * configure which aspect ratio to use.
 *
 * @param    {Object}         media    The media item
 * @param    {Object}         options  The artwork options
 * @returns  {Array<string>}  The url of the poster
 */
createPoster = function(media, options) {
    var posters     = [],
        aspectRatio = ThumbnailVariables.getAspectRatio(options.posterRatio || "portrait"),
        poster      = findBestFittingCover(media, aspectRatio);

    if (poster && imageUrlGenerator) {
        posters.push(imageUrlGenerator(
            relativeImageUrlToAbsoluteUrl(poster.url),
            themeVars.windowWidth,
            Math.ceil(themeVars.windowWidth / aspectRatio)
        ));
    }

    return posters;
};

/**
 * Creates a url for the thumbnail for Radiant MP. Options object can be used to
 * configure which aspect ratio to use.
 *
 * @param    {Object}       media    The media item
 * @param    {Object}       options  The artwork options
 * @returns  {String|null}  The url for the thumbnail
 */
createThumbnail = function(media, options) {
    var aspectRatio = ThumbnailVariables.getAspectRatio(options.thumbnailRatio || "landscape"),
        thumbnail   = findBestFittingThumbnail(media, aspectRatio);

    if (thumbnail && imageUrlGenerator) {
        return imageUrlGenerator(
            relativeImageUrlToAbsoluteUrl(thumbnail.url),
            themeVars.windowWidth,
            Math.ceil(themeVars.windowWidth / aspectRatio)
        );
    }

    return null;
};

/**
 * Creates an array of end of video poster for the posters for Radiant MP at
 * various aspect ratios. Radiant MP will choose the best one to use on it's
 * own.
 *
 * @returns  {Array<string>}  The urls of the posters
 */
createEndOfVideoPoster = function() {
    var posters = [],
        aspectRatio,
        poster;

    ["portrait", "landscape", "square"].forEach(function(formFactor) {
        aspectRatio = ThumbnailVariables.getAspectRatio(formFactor);
        poster      = findBestFittingPlaceholder(placeholders, aspectRatio);

        if (poster && imageUrlGenerator) {
            posters.push(imageUrlGenerator(
                relativeImageUrlToAbsoluteUrl(poster.url),
                themeVars.windowWidth,
                Math.ceil(themeVars.windowWidth / aspectRatio)
            ));
        }
    });

    return posters;
};
